import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Code2, Download, ExternalLink, FileJson, Zap, Server, Globe, Key } from 'lucide-react';

const PublicAPIDocsPage = () => {
  const base = window.location.origin;
  const [endpointCount, setEndpointCount] = useState(null);

  useEffect(() => {
    document.title = 'OkBot — Documentación API pública';
    fetch(`${base}/api/openapi.json`)
      .then(r => r.json())
      .then(spec => {
        try {
          const paths = Object.keys(spec.paths || {});
          let total = 0;
          paths.forEach(p => { total += Object.keys(spec.paths[p] || {}).length; });
          setEndpointCount(total);
        } catch (_) {}
      })
      .catch(() => {});
  }, [base]);

  const cards = [
    {
      icon: BookOpen,
      title: 'Swagger UI interactivo',
      desc: 'Explora cada endpoint, prueba peticiones en vivo y revisa los esquemas de request/response.',
      href: `${base}/api/docs`,
      color: 'from-emerald-500 to-teal-600',
      cta: 'Abrir Swagger UI',
    },
    {
      icon: Code2,
      title: 'ReDoc — referencia limpia',
      desc: 'Documentación tipo libro, ideal para integradores y equipos que necesitan leer la API.',
      href: `${base}/api/redoc`,
      color: 'from-indigo-500 to-blue-600',
      cta: 'Abrir ReDoc',
    },
    {
      icon: FileJson,
      title: 'Esquema OpenAPI 3.0 (JSON)',
      desc: 'Descarga el contrato completo para generar SDKs, importarlo en Postman o conectarlo a Bubble.io.',
      href: `${base}/api/openapi.json`,
      color: 'from-orange-500 to-red-500',
      cta: 'Descargar JSON',
    },
  ];

  const quickStart = [
    { title: 'Autenticación', body: 'Usa un token Bearer JWT obtenido en POST /api/auth/login. Adjúntalo en el header Authorization.' },
    { title: 'Multi-rol', body: 'OkBot soporta usuarios, negocios y administradores. Cada rol expone endpoints adicionales bajo su prefijo.' },
    { title: 'Webhooks WhatsApp', body: 'POST /api/kapso/webhook/receive recibe mensajes de WhatsApp (texto y audios) y dispara los agentes IA.' },
    { title: 'Function-calling', body: 'Los agentes ejecutan herramientas MCP reales: crear notas, eventos, recordatorios, registros financieros y más.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-md bg-slate-950/40 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-white/70 hover:text-white">
            <ArrowLeft size={16} /> Volver al inicio
          </Link>
          <div className="flex items-center gap-2">
            <img src="/okbot_dark_theme.png" alt="OkBot" className="h-8 w-8 rounded-lg" />
            <span className="font-bold tracking-tight">OkBot · API Docs</span>
          </div>
          <a href="/api/docs" target="_blank" rel="noreferrer"
             className="text-sm px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition flex items-center gap-1">
            <ExternalLink size={14} /> Swagger
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-xs mb-4">
          <Zap size={12} /> API REST pública · OpenAPI 3.0
        </div>
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight !text-white">
          Conecta cualquier plataforma a <span className="bg-clip-text !text-transparent bg-gradient-to-r from-emerald-400 to-cyan-400">OkBot</span>
        </h1>
        <p className="mt-5 text-lg text-white/70 max-w-2xl mx-auto">
          Más de <span className="text-white font-semibold">{endpointCount ? `${endpointCount}` : '200'}</span> endpoints REST documentados.
          Explora la API en vivo, descarga el esquema OpenAPI, integra con Bubble.io, Zapier o tu propio backend.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href="/api/docs" target="_blank" rel="noreferrer"
             className="px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold flex items-center gap-2 transition">
            <BookOpen size={18} /> Abrir Swagger UI
          </a>
          <a href="/api/openapi.json" download
             className="px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 font-semibold flex items-center gap-2 transition">
            <Download size={18} /> Descargar OpenAPI 3.0
          </a>
        </div>
      </section>

      {/* Cards */}
      <section className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-5">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <a key={c.title} href={c.href} target="_blank" rel="noreferrer"
               className="group relative rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition p-6">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-4`}>
                <Icon size={22} />
              </div>
              <h3 className="text-lg font-bold !text-white">{c.title}</h3>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">{c.desc}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-emerald-300 text-sm font-semibold group-hover:gap-2 transition-all">
                {c.cta} <ExternalLink size={14} />
              </div>
            </a>
          );
        })}
      </section>

      {/* Quick start */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 !text-white">Conecta en minutos</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {quickStart.map(q => (
            <div key={q.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center gap-2 mb-2">
                <Key size={16} className="text-emerald-300" />
                <h3 className="font-semibold !text-white">{q.title}</h3>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">{q.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* curl example */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold mb-3 flex items-center gap-2 !text-white"><Server size={18} /> Ejemplo rápido</h2>
        <pre className="rounded-xl bg-slate-950 border border-white/10 p-5 text-sm overflow-x-auto text-emerald-300">
{`# 1) Login
curl -X POST ${base}/api/auth/login \\
  -H 'Content-Type: application/json' \\
  -d '{"username":"tu_usuario","password":"***"}'

# 2) Crear una nota
curl -X POST ${base}/api/notes/v2?user_id=USER_UUID \\
  -H 'Authorization: Bearer JWT' \\
  -H 'Content-Type: application/json' \\
  -d '{"title":"Mi primera nota","content":"Hola OkBot"}'`}
        </pre>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-12 py-8 text-center text-sm text-white/50">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Globe size={14} /> {base}
        </div>
        © {new Date().getFullYear()} OkBot · Sistema Operativo Personal con IA
      </footer>
    </div>
  );
};

export default PublicAPIDocsPage;
